import { useCallback, useEffect, useMemo, useState } from 'react';
import { Title, useGetIdentity, useNotify } from 'react-admin';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SaveIcon from '@mui/icons-material/Save';
import DownloadIcon from '@mui/icons-material/Download';
import { adsProxy, expertTariffsProxy } from './apiGateway';

type PriceRange = {
    key: string;
    label: string;
    minExclusive: number | null;
    maxInclusive: number | null;
};

type CategoryByGenderItem = {
    id: string;
    title: string;
    categories: CategoryItem[];
};

type CategoryItem = {
    id: string;
    title: string;
    subcategories: SubcategoryItem[];
};

type SubcategoryItem = {
    id: string;
    title: string;
};

export type EntityTitleMaps = {
    categoryTitlesById: Record<string, string>;
    subcategoryTitlesById: Record<string, string>;
};

export type TariffActor = {
    userId?: string;
    login?: string;
    fullName?: string;
} | null;

export type TariffHistoryItem = {
    id: string;
    createdAt: string | null;
    updatedBy: TariffActor;
    categoryCount: number;
    subcategoryCount: number;
};

export type TariffHistoryRevision = {
    id: string;
    createdAt: string | null;
    updatedAt: string | null;
    updatedBy: TariffActor;
    defaultTariffRub: number;
    categoryTariffs: TariffNumberMap;
    subcategoryTariffs: TariffNumberMap;
    priceRanges: PriceRange[];
};

type TariffInputMap = Record<string, Record<string, string>>;
type TariffNumberMap = Record<string, Record<string, number>>;

const DEFAULT_PRICE_RANGES: PriceRange[] = [
    { key: 'UP_TO_5K', label: 'до 5к', minExclusive: null, maxInclusive: 5000 },
    { key: 'FROM_5K_TO_10K', label: '5к - 10к', minExclusive: 5000, maxInclusive: 10000 },
    { key: 'FROM_10K_TO_20K', label: '10к - 20к', minExclusive: 10000, maxInclusive: 20000 },
    { key: 'FROM_20K_TO_50K', label: '20к - 50к', minExclusive: 20000, maxInclusive: 50000 },
    { key: 'FROM_50K_TO_100K', label: '50к - 100к', minExclusive: 50000, maxInclusive: 100000 },
    { key: 'FROM_100K_TO_200K', label: '100к - 200к', minExclusive: 100000, maxInclusive: 200000 },
    { key: 'FROM_200K_TO_500K', label: '200к - 500к', minExclusive: 200000, maxInclusive: 500000 },
    { key: 'FROM_500K', label: 'от 500к', minExclusive: 500000, maxInclusive: null }
];

function normalizeEntityKey(value: string | null | undefined) {
    return typeof value === 'string' ? value.trim() : '';
}

function addEntityTitle(map: Record<string, string>, rawKey: string | null | undefined, rawTitle: string | null | undefined) {
    const key = normalizeEntityKey(rawKey);
    const title = normalizeEntityKey(rawTitle);
    if (!key || !title) {
        return;
    }
    map[key] = title;
    map[key.toLowerCase()] = title;
}

export function buildEntityTitleMaps(
    categoriesByGender:
        | Array<{
            categories?: Array<{
                id?: string;
                title?: string;
                subcategories?: Array<{ id?: string; title?: string }>;
            }>;
        }>
        | null
        | undefined
): EntityTitleMaps {
    const categoryTitlesById: Record<string, string> = {};
    const subcategoryTitlesById: Record<string, string> = {};

    const safeCategoriesByGender = Array.isArray(categoriesByGender) ? categoriesByGender : [];
    safeCategoriesByGender.forEach((genderItem) => {
        const categories = Array.isArray(genderItem?.categories) ? genderItem.categories : [];
        categories.forEach((category) => {
            addEntityTitle(categoryTitlesById, category?.id, category?.title);
            addEntityTitle(categoryTitlesById, category?.title, category?.title);
            const subcategories = Array.isArray(category?.subcategories) ? category.subcategories : [];
            subcategories.forEach((subcategory) => {
                addEntityTitle(subcategoryTitlesById, subcategory?.id, subcategory?.title);
                addEntityTitle(subcategoryTitlesById, subcategory?.title, subcategory?.title);
            });
        });
    });

    return {
        categoryTitlesById,
        subcategoryTitlesById
    };
}

function resolveEntityTitle(entityKey: string, map?: Record<string, string>) {
    const normalizedKey = normalizeEntityKey(entityKey);
    if (!normalizedKey || !map) {
        return normalizedKey || entityKey;
    }
    return map[normalizedKey] || map[normalizedKey.toLowerCase()] || normalizedKey;
}

function convertTariffNumbersToInputs(source: TariffNumberMap | null | undefined): TariffInputMap {
    if (!source || typeof source !== 'object') {
        return {};
    }

    const input: TariffInputMap = {};
    Object.entries(source).forEach(([rawKey, rawRanges]) => {
        const entityKey = normalizeEntityKey(rawKey);
        if (!entityKey || !rawRanges || typeof rawRanges !== 'object') {
            return;
        }

        const ranges: Record<string, string> = {};
        Object.entries(rawRanges).forEach(([rangeKey, rawValue]) => {
            const numberValue = Number(rawValue);
            if (!Number.isFinite(numberValue) || numberValue < 0) {
                return;
            }
            ranges[rangeKey] = String(Math.round(numberValue));
        });

        if (Object.keys(ranges).length) {
            input[entityKey] = ranges;
        }
    });
    return input;
}

function parseTariffInputs(source: TariffInputMap, entityLabel: string): TariffNumberMap {
    const parsed: TariffNumberMap = {};

    Object.entries(source).forEach(([rawEntityKey, rawRanges]) => {
        const entityKey = normalizeEntityKey(rawEntityKey);
        if (!entityKey || !rawRanges || typeof rawRanges !== 'object') {
            return;
        }

        const sanitizedRanges: Record<string, number> = {};
        Object.entries(rawRanges).forEach(([rangeKey, rawInputValue]) => {
            const normalizedInputValue = String(rawInputValue ?? '').trim();
            if (!normalizedInputValue) {
                return;
            }

            const parsedValue = Number(normalizedInputValue.replace(',', '.'));
            if (!Number.isFinite(parsedValue) || parsedValue < 0) {
                throw new Error(`Некорректная цена в ${entityLabel} (${entityKey}, диапазон ${rangeKey})`);
            }
            sanitizedRanges[rangeKey] = Math.round(parsedValue);
        });

        if (Object.keys(sanitizedRanges).length) {
            parsed[entityKey] = sanitizedRanges;
        }
    });

    return parsed;
}

function getParsedInputValue(inputMap: TariffInputMap, entityKey: string, rangeKey: string): number | null {
    const rawValue = inputMap[entityKey]?.[rangeKey];
    const cleanedValue = typeof rawValue === 'string' ? rawValue.trim() : '';
    if (!cleanedValue) {
        return null;
    }
    const parsedValue = Number(cleanedValue.replace(',', '.'));
    if (!Number.isFinite(parsedValue) || parsedValue < 0) {
        return null;
    }
    return Math.round(parsedValue);
}

function formatDateTime(value: string | null) {
    if (!value) {
        return 'не обновлялось';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return 'неизвестно';
    }
    return date.toLocaleString('ru-RU');
}

export function formatTariffHistoryActorLabel(actor: TariffActor) {
    const normalizedActor = actor || null;
    const fullName = normalizeEntityKey(normalizedActor?.fullName);
    const login = normalizeEntityKey(normalizedActor?.login);
    return [fullName, login].filter(Boolean).join(' / ') || '-';
}

function sanitizeCanvasText(value: string) {
    return String(value || '').replace(/\r?\n/g, ' ').trim();
}

function trimCanvasTextToWidth(ctx: CanvasRenderingContext2D, value: string, maxWidth: number) {
    const normalized = sanitizeCanvasText(value);
    if (!normalized) {
        return '-';
    }
    if (ctx.measureText(normalized).width <= maxWidth) {
        return normalized;
    }

    let trimmed = normalized;
    while (trimmed.length > 1 && ctx.measureText(`${trimmed}...`).width > maxWidth) {
        trimmed = trimmed.slice(0, -1);
    }
    return `${trimmed}...`;
}

function jpegDataUrlToBytes(dataUrl: string) {
    const base64 = String(dataUrl || '').split(',')[1] || '';
    const binary = window.atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let index = 0; index < binary.length; index += 1) {
        bytes[index] = binary.charCodeAt(index);
    }
    return bytes;
}

function concatUint8Arrays(chunks: Uint8Array[]) {
    const totalLength = chunks.reduce((sum, chunk) => sum + chunk.length, 0);
    const result = new Uint8Array(totalLength);
    let offset = 0;
    chunks.forEach((chunk) => {
        result.set(chunk, offset);
        offset += chunk.length;
    });
    return result;
}

type PdfJpegPage = {
    jpegBytes: Uint8Array;
    widthPx: number;
    heightPx: number;
};

function buildPdfFromJpegPages(pages: PdfJpegPage[]) {
    const safePages = pages.length ? pages : [{
        jpegBytes: new Uint8Array(),
        widthPx: 1,
        heightPx: 1
    }];
    const encoder = new TextEncoder();
    const pageCount = safePages.length;
    const maxObjId = 2 + pageCount * 3;
    const chunks: Uint8Array[] = [];
    const offsets = new Array(maxObjId + 1).fill(0);
    let currentOffset = 0;

    const pushString = (value: string) => {
        const bytes = encoder.encode(value);
        chunks.push(bytes);
        currentOffset += bytes.length;
    };

    const pushBytes = (value: Uint8Array) => {
        chunks.push(value);
        currentOffset += value.length;
    };

    const pageWidthPt = 842;
    const pageHeightPt = 595;

    pushString('%PDF-1.4\n');

    offsets[1] = currentOffset;
    pushString('1 0 obj\n<< /Type /Catalog /Pages 2 0 R >>\nendobj\n');

    const pageObjectIds: number[] = [];
    for (let pageIndex = 0; pageIndex < pageCount; pageIndex += 1) {
        pageObjectIds.push(5 + pageIndex * 3);
    }
    offsets[2] = currentOffset;
    pushString(`2 0 obj\n<< /Type /Pages /Kids [${pageObjectIds.map((id) => `${id} 0 R`).join(' ')}] /Count ${pageCount} >>\nendobj\n`);

    for (let pageIndex = 0; pageIndex < pageCount; pageIndex += 1) {
        const imageObjId = 3 + pageIndex * 3;
        const contentObjId = 4 + pageIndex * 3;
        const pageObjId = 5 + pageIndex * 3;
        const page = safePages[pageIndex];

        offsets[imageObjId] = currentOffset;
        pushString(`${imageObjId} 0 obj\n`);
        pushString(`<< /Type /XObject /Subtype /Image /Width ${Math.max(1, Math.round(page.widthPx))} /Height ${Math.max(1, Math.round(page.heightPx))} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${page.jpegBytes.length} >>\n`);
        pushString('stream\n');
        pushBytes(page.jpegBytes);
        pushString('\nendstream\nendobj\n');

        const contentStream = `q\n${pageWidthPt} 0 0 ${pageHeightPt} 0 0 cm\n/Im0 Do\nQ\n`;
        offsets[contentObjId] = currentOffset;
        pushString(`${contentObjId} 0 obj\n<< /Length ${encoder.encode(contentStream).length} >>\nstream\n${contentStream}endstream\nendobj\n`);

        offsets[pageObjId] = currentOffset;
        pushString(`${pageObjId} 0 obj\n<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${pageWidthPt} ${pageHeightPt}] /Resources << /XObject << /Im0 ${imageObjId} 0 R >> >> /Contents ${contentObjId} 0 R >>\nendobj\n`);
    }

    const xrefOffset = currentOffset;
    pushString(`xref\n0 ${maxObjId + 1}\n`);
    pushString('0000000000 65535 f \n');
    for (let objectId = 1; objectId <= maxObjId; objectId += 1) {
        pushString(`${String(offsets[objectId]).padStart(10, '0')} 00000 n \n`);
    }
    pushString(`trailer\n<< /Size ${maxObjId + 1} /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`);

    return new Blob([concatUint8Arrays(chunks)], { type: 'application/pdf' });
}

export function buildTariffHistoryRevisionPdfBlob(revision: TariffHistoryRevision, entityTitles?: EntityTitleMaps) {
    const priceRanges = Array.isArray(revision.priceRanges) && revision.priceRanges.length
        ? revision.priceRanges
        : DEFAULT_PRICE_RANGES;
    const rangeDescriptors = priceRanges.map((range) => ({
        key: sanitizeCanvasText(range.key),
        label: sanitizeCanvasText(range.label || range.key)
    }));
    const rangeHeaders = rangeDescriptors.map((range) => range.label);
    const categoryEntries = Object.entries(revision.categoryTariffs || {}).sort(([left], [right]) => left.localeCompare(right));
    const subcategoryEntries = Object.entries(revision.subcategoryTariffs || {}).sort(([left], [right]) => left.localeCompare(right));
    const defaultTariff = Number.isFinite(Number(revision.defaultTariffRub))
        ? Math.round(Number(revision.defaultTariffRub))
        : 200;

    const sections = [
        {
            title: `Тарифы категорий (${categoryEntries.length})`,
            rows: categoryEntries.map(([entityKey, rangeValues]) => ({
                entityName: sanitizeCanvasText(resolveEntityTitle(entityKey, entityTitles?.categoryTitlesById)),
                values: rangeDescriptors.map((range) => {
                    const rangeKey = range.key;
                    const value = Number(rangeValues?.[rangeKey]);
                    return Number.isFinite(value) ? String(Math.round(value)) : '-';
                })
            }))
        },
        {
            title: `Тарифы подкатегорий (${subcategoryEntries.length})`,
            rows: subcategoryEntries.map(([entityKey, rangeValues]) => ({
                entityName: sanitizeCanvasText(resolveEntityTitle(entityKey, entityTitles?.subcategoryTitlesById)),
                values: rangeDescriptors.map((range) => {
                    const rangeKey = range.key;
                    const value = Number(rangeValues?.[rangeKey]);
                    return Number.isFinite(value) ? String(Math.round(value)) : '-';
                })
            }))
        }
    ];

    const canvasWidth = 1684;
    const canvasHeight = 1191;
    const marginLeft = 40;
    const marginRight = 40;
    const marginTop = 40;
    const marginBottom = 40;
    const rowHeight = 28;
    const tableWidth = canvasWidth - marginLeft - marginRight;
    const firstColWidth = 520;
    const fallbackRangeCols = Math.max(1, rangeHeaders.length);
    const rangeColWidth = (tableWidth - firstColWidth) / fallbackRangeCols;
    const pageCanvases: HTMLCanvasElement[] = [];

    const createPage = () => {
        const canvas = document.createElement('canvas');
        canvas.width = canvasWidth;
        canvas.height = canvasHeight;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
            throw new Error('Не удалось инициализировать canvas для PDF');
        }
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvasWidth, canvasHeight);
        ctx.strokeStyle = '#2f2f2f';
        ctx.fillStyle = '#111111';
        ctx.textBaseline = 'middle';
        pageCanvases.push(canvas);
        return ctx;
    };

    let ctx = createPage();
    let currentY = marginTop;

    const ensureSpace = (requiredHeight: number) => {
        if (currentY + requiredHeight <= canvasHeight - marginBottom) {
            return false;
        }
        ctx = createPage();
        currentY = marginTop;
        return true;
    };

    const drawTextCell = (
        text: string,
        x: number,
        y: number,
        width: number,
        font: string,
        color = '#111111'
    ) => {
        ctx.font = font;
        ctx.fillStyle = color;
        const value = trimCanvasTextToWidth(ctx, text, Math.max(0, width - 8));
        ctx.fillText(value, x + 4, y + rowHeight / 2);
    };

    const drawTableHeader = () => {
        ctx.fillStyle = '#f1f4f8';
        ctx.fillRect(marginLeft, currentY, tableWidth, rowHeight);
        ctx.strokeStyle = '#2f2f2f';
        ctx.strokeRect(marginLeft, currentY, tableWidth, rowHeight);

        let x = marginLeft;
        ctx.strokeRect(x, currentY, firstColWidth, rowHeight);
        drawTextCell('Категория / подкатегория', x, currentY, firstColWidth, '600 14px Arial', '#1d1d1d');
        x += firstColWidth;

        rangeHeaders.forEach((header) => {
            ctx.strokeRect(x, currentY, rangeColWidth, rowHeight);
            drawTextCell(header, x, currentY, rangeColWidth, '600 13px Arial', '#1d1d1d');
            x += rangeColWidth;
        });
        currentY += rowHeight;
    };

    const drawDataRow = (entityName: string, values: string[]) => {
        ctx.strokeStyle = '#2f2f2f';
        ctx.strokeRect(marginLeft, currentY, tableWidth, rowHeight);

        let x = marginLeft;
        ctx.strokeRect(x, currentY, firstColWidth, rowHeight);
        drawTextCell(entityName, x, currentY, firstColWidth, '13px Arial');
        x += firstColWidth;

        values.forEach((value) => {
            ctx.strokeRect(x, currentY, rangeColWidth, rowHeight);
            drawTextCell(value, x, currentY, rangeColWidth, '13px Arial');
            x += rangeColWidth;
        });
        currentY += rowHeight;
    };

    const metadataLines = [
        'Редакция тарифов экспертов',
        `ID редакции: ${sanitizeCanvasText(revision.id)}`,
        `Дата редакции: ${sanitizeCanvasText(revision.createdAt || '-')}`,
        `Редактор: ${sanitizeCanvasText(formatTariffHistoryActorLabel(revision.updatedBy))}`,
        `Тариф по умолчанию (RUB): ${defaultTariff}`,
        `Диапазоны: ${rangeHeaders.join(', ')}`
    ];

    metadataLines.forEach((line, index) => {
        ensureSpace(24);
        ctx.font = index === 0 ? '700 24px Arial' : '16px Arial';
        ctx.fillStyle = '#111111';
        const trimmed = trimCanvasTextToWidth(ctx, line, tableWidth);
        ctx.fillText(trimmed, marginLeft, currentY + 12);
        currentY += index === 0 ? 34 : 24;
    });
    currentY += 8;

    sections.forEach((section) => {
        ensureSpace(52);
        ctx.font = '700 18px Arial';
        ctx.fillStyle = '#111111';
        const sectionTitle = sanitizeCanvasText(section.title);
        ctx.fillText(sectionTitle, marginLeft, currentY + 12);
        currentY += 24;
        drawTableHeader();

        if (!section.rows.length) {
            ensureSpace(rowHeight);
            drawDataRow('Нет данных', new Array(fallbackRangeCols).fill('-'));
            currentY += 12;
            return;
        }

        section.rows.forEach((row) => {
            if (ensureSpace(rowHeight)) {
                ctx.font = '700 17px Arial';
                ctx.fillStyle = '#111111';
                ctx.fillText(`${sectionTitle} (продолжение)`, marginLeft, currentY + 10);
                currentY += 22;
                drawTableHeader();
            }

            const paddedValues = [...row.values];
            while (paddedValues.length < fallbackRangeCols) {
                paddedValues.push('-');
            }
            drawDataRow(row.entityName, paddedValues.slice(0, fallbackRangeCols));
        });

        currentY += 12;
    });

    const jpegPages: PdfJpegPage[] = pageCanvases.map((canvas) => ({
        jpegBytes: jpegDataUrlToBytes(canvas.toDataURL('image/jpeg', 0.92)),
        widthPx: canvas.width,
        heightPx: canvas.height
    }));

    return buildPdfFromJpegPages(jpegPages);
}

export const ExpertTariffsPage = () => {
    const notify = useNotify();
    const { identity, isPending } = useGetIdentity();
    const currentUser = (identity ?? null) as { role?: string } | null;
    const isManagerAdmin = currentUser?.role === 'manager-admin';

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [categoriesByGender, setCategoriesByGender] = useState<CategoryByGenderItem[]>([]);
    const [priceRanges, setPriceRanges] = useState<PriceRange[]>(DEFAULT_PRICE_RANGES);
    const [defaultTariffRub, setDefaultTariffRub] = useState(200);
    const [updatedAt, setUpdatedAt] = useState<string | null>(null);
    const [updatedByLabel, setUpdatedByLabel] = useState<string>('');
    const [categoryTariffsInput, setCategoryTariffsInput] = useState<TariffInputMap>({});
    const [subcategoryTariffsInput, setSubcategoryTariffsInput] = useState<TariffInputMap>({});
    const [history, setHistory] = useState<TariffHistoryItem[]>([]);
    const [expandedGenderPanel, setExpandedGenderPanel] = useState<string | false>(false);
    const [expandedCategoryPanelByGender, setExpandedCategoryPanelByGender] = useState<Record<string, string | false>>({});
    const [downloadingRevisionId, setDownloadingRevisionId] = useState<string | null>(null);

    const loadData = useCallback(async () => {
        if (!isManagerAdmin) {
            setLoading(false);
            return;
        }

        setLoading(true);
        try {
            const [tariffsResponse, adConfigResponse, historyResponse] = await Promise.all([
                expertTariffsProxy.getConfig(),
                adsProxy.getAdConfig(),
                expertTariffsProxy.getHistory(100)
            ]);

            const adConfigBody = (adConfigResponse?.body ?? adConfigResponse) as {
                categoriesByGender?: CategoryByGenderItem[];
            };
            const resolvedCategoriesByGender = Array.isArray(adConfigBody?.categoriesByGender)
                ? adConfigBody.categoriesByGender
                : [];
            const resolvedPriceRanges = Array.isArray(tariffsResponse?.priceRanges) && tariffsResponse.priceRanges.length
                ? (tariffsResponse.priceRanges as PriceRange[])
                : DEFAULT_PRICE_RANGES;

            const updatedBy = tariffsResponse?.updatedBy || null;
            const updatedByLabelValue = [
                normalizeEntityKey(updatedBy?.fullName),
                normalizeEntityKey(updatedBy?.login)
            ].filter(Boolean).join(' / ');

            setCategoriesByGender(resolvedCategoriesByGender);
            setPriceRanges(resolvedPriceRanges);
            setDefaultTariffRub(Number.isFinite(Number(tariffsResponse?.defaultTariffRub))
                ? Math.round(Number(tariffsResponse.defaultTariffRub))
                : 200);
            setUpdatedAt(typeof tariffsResponse?.updatedAt === 'string' ? tariffsResponse.updatedAt : null);
            setUpdatedByLabel(updatedByLabelValue);
            setCategoryTariffsInput(convertTariffNumbersToInputs(tariffsResponse?.categoryTariffs as TariffNumberMap));
            setSubcategoryTariffsInput(convertTariffNumbersToInputs(tariffsResponse?.subcategoryTariffs as TariffNumberMap));
            setHistory(Array.isArray(historyResponse?.data) ? historyResponse.data as TariffHistoryItem[] : []);
            setExpandedGenderPanel(false);
            setExpandedCategoryPanelByGender({});
        } catch (error) {
            console.error('Failed to load expert tariffs page data:', error);
            notify('Не удалось загрузить тарифы для экспертов', { type: 'error' });
        } finally {
            setLoading(false);
        }
    }, [isManagerAdmin, notify]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const entityTitleMaps = useMemo(
        () => buildEntityTitleMaps(categoriesByGender),
        [categoriesByGender]
    );

    const handleCategoryTariffInputChange = (entityKey: string, rangeKey: string, value: string) => {
        setCategoryTariffsInput((prev) => ({
            ...prev,
            [entityKey]: {
                ...(prev[entityKey] || {}),
                [rangeKey]: value
            }
        }));
    };

    const handleSubcategoryTariffInputChange = (entityKey: string, rangeKey: string, value: string) => {
        setSubcategoryTariffsInput((prev) => ({
            ...prev,
            [entityKey]: {
                ...(prev[entityKey] || {}),
                [rangeKey]: value
            }
        }));
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const categoryTariffs = parseTariffInputs(categoryTariffsInput, 'категории');
            const subcategoryTariffs = parseTariffInputs(subcategoryTariffsInput, 'подкатегории');

            const response = await expertTariffsProxy.updateConfig({
                categoryTariffs,
                subcategoryTariffs
            });
            const historyResponse = await expertTariffsProxy.getHistory(100);

            const updatedBy = response?.updatedBy || null;
            const updatedByLabelValue = [
                normalizeEntityKey(updatedBy?.fullName),
                normalizeEntityKey(updatedBy?.login)
            ].filter(Boolean).join(' / ');

            setUpdatedAt(typeof response?.updatedAt === 'string' ? response.updatedAt : null);
            setUpdatedByLabel(updatedByLabelValue);
            setCategoryTariffsInput(convertTariffNumbersToInputs(response?.categoryTariffs as TariffNumberMap));
            setSubcategoryTariffsInput(convertTariffNumbersToInputs(response?.subcategoryTariffs as TariffNumberMap));
            setHistory(Array.isArray(historyResponse?.data) ? historyResponse.data as TariffHistoryItem[] : []);
            notify('Тарифы сохранены', { type: 'info' });
        } catch (error) {
            const message = error instanceof Error && error.message.trim()
                ? error.message
                : 'Не удалось сохранить тарифы';
            notify(message, { type: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const handleDownloadRevisionPdf = async (historyItem: TariffHistoryItem) => {
        const previewWindow = window.open('about:blank', '_blank');
        if (!previewWindow) {
            notify('Браузер заблокировал открытие вкладки. Разрешите pop-up.', { type: 'warning' });
            return;
        }

        previewWindow.document.write('<p>Формируем PDF...</p>');
        previewWindow.document.close();
        setDownloadingRevisionId(historyItem.id);
        try {
            const response = await expertTariffsProxy.getHistoryRevision(historyItem.id);
            const revision = response?.data as TariffHistoryRevision | undefined;
            if (!revision) {
                throw new Error('Не удалось получить данные ревизии');
            }

            const blob = buildTariffHistoryRevisionPdfBlob(revision, entityTitleMaps);
            const url = URL.createObjectURL(blob);
            previewWindow.location.href = url;
            window.setTimeout(() => {
                URL.revokeObjectURL(url);
            }, 60_000);
        } catch (error) {
            previewWindow.close();
            const message = error instanceof Error && error.message.trim()
                ? error.message
                : 'Не удалось сформировать PDF';
            notify(message, { type: 'error' });
        } finally {
            setDownloadingRevisionId(null);
        }
    };

    const summaryLabel = useMemo(() => {
        const categoriesCount = categoriesByGender.reduce((acc, gender) => {
            const categories = Array.isArray(gender.categories) ? gender.categories : [];
            return acc + categories.length;
        }, 0);
        const subcategoriesCount = categoriesByGender.reduce((acc, gender) => {
            const categories = Array.isArray(gender.categories) ? gender.categories : [];
            return acc + categories.reduce((innerAcc, category) => {
                const subcategories = Array.isArray(category.subcategories) ? category.subcategories : [];
                return innerAcc + subcategories.length;
            }, 0);
        }, 0);
        return `Категорий: ${categoriesCount}, подкатегорий: ${subcategoriesCount}`;
    }, [categoriesByGender]);

    if (isPending || loading) {
        return (
            <Box sx={{ p: 3 }}>
                <Title title="Тарифы для экспертов" />
                <CircularProgress size={28} />
            </Box>
        );
    }

    if (!isManagerAdmin) {
        return (
            <Box sx={{ p: 3 }}>
                <Title title="Тарифы для экспертов" />
                <Alert severity="error">Доступ только для роли manager-admin</Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ p: { xs: 2, md: 3 } }}>
            <Title title="Тарифы для экспертов" />
            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} sx={{ mb: 2, gap: 1.5 }}>
                <Box>
                    <Typography variant="h4">Тарифы для экспертов</Typography>
                    <Typography variant="body2" color="text.secondary">{summaryLabel}</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Обновлено: {formatDateTime(updatedAt)}{updatedByLabel ? ` (${updatedByLabel})` : ''}
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={handleSave}
                    disabled={saving}
                >
                    {saving ? 'Сохраняю...' : 'Сохранить тарифы'}
                </Button>
            </Stack>

            <Alert severity="info" sx={{ mb: 2 }}>
                Если цена для подкатегории не заполнена, используется цена категории. Если у категории цена не заполнена, используется тариф по умолчанию {defaultTariffRub} ₽.
            </Alert>

            <Accordion sx={{ mb: 2 }} defaultExpanded={false}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h6">История тарифов ({history.length})</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    {history.length === 0 ? (
                        <Alert severity="info">История пока пустая. Появится после первого сохранения тарифов.</Alert>
                    ) : (
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Редакция от</TableCell>
                                        <TableCell>Редактор</TableCell>
                                        <TableCell align="right">Категорий</TableCell>
                                        <TableCell align="right">Подкатегорий</TableCell>
                                        <TableCell align="right">Файл</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {history.map((historyItem, index) => (
                                        <TableRow key={historyItem.id}>
                                            <TableCell>
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <span>{formatDateTime(historyItem.createdAt)}</span>
                                                    {index === 0 ? (
                                                        <Chip size="small" color="success" variant="outlined" label="Актуальная" />
                                                    ) : null}
                                                </Stack>
                                            </TableCell>
                                            <TableCell>{formatTariffHistoryActorLabel(historyItem.updatedBy)}</TableCell>
                                            <TableCell align="right">{historyItem.categoryCount ?? 0}</TableCell>
                                            <TableCell align="right">{historyItem.subcategoryCount ?? 0}</TableCell>
                                            <TableCell align="right">
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    startIcon={downloadingRevisionId === historyItem.id ? <CircularProgress size={14} /> : <DownloadIcon />}
                                                    onClick={() => handleDownloadRevisionPdf(historyItem)}
                                                    disabled={downloadingRevisionId === historyItem.id}
                                                >
                                                    {downloadingRevisionId === historyItem.id ? 'Формирую...' : 'Скачать PDF'}
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </AccordionDetails>
            </Accordion>

            {categoriesByGender.length === 0 ? (
                <Alert severity="warning">Не удалось получить категории из adConfig.</Alert>
            ) : (
                <Stack spacing={2}>
                    {categoriesByGender.map((genderItem) => {
                        const genderCategories = Array.isArray(genderItem.categories) ? genderItem.categories : [];
                        const genderPanelKey = genderItem.id || genderItem.title || 'unknown';
                        const isExpanded = expandedGenderPanel === genderPanelKey;
                        const expandedCategoryPanel = expandedCategoryPanelByGender[genderPanelKey] || false;
                        return (
                            <Accordion
                                key={genderPanelKey}
                                expanded={isExpanded}
                                onChange={(_, expanded) => setExpandedGenderPanel(expanded ? genderPanelKey : false)}
                                slotProps={{ transition: { unmountOnExit: true } }}
                            >
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Typography variant="h6">{genderItem.title || genderItem.id || 'Без названия'}</Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    {!isExpanded ? null : genderCategories.length === 0 ? (
                                        <Alert severity="info">Для этого раздела нет категорий.</Alert>
                                    ) : (
                                        <Stack spacing={1.5}>
                                            {genderCategories.map((category, categoryIndex) => {
                                                const categoryKey = normalizeEntityKey(category.id || category.title);
                                                const subcategories = Array.isArray(category.subcategories)
                                                    ? category.subcategories
                                                    : [];
                                                const categoryPanelKey = `${genderPanelKey}::${category.id || category.title || categoryIndex}`;
                                                const isCategoryExpanded = expandedCategoryPanel === categoryPanelKey;

                                                return (
                                                    <Accordion
                                                        key={categoryPanelKey}
                                                        expanded={isCategoryExpanded}
                                                        onChange={(_, expanded) => {
                                                            setExpandedCategoryPanelByGender((prev) => ({
                                                                ...prev,
                                                                [genderPanelKey]: expanded ? categoryPanelKey : false
                                                            }));
                                                        }}
                                                        slotProps={{ transition: { unmountOnExit: true } }}
                                                    >
                                                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ width: '100%', pr: 1 }}>
                                                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                                                    {category.title || category.id || 'Категория без названия'}
                                                                </Typography>
                                                                <Typography variant="body2" color="text.secondary">
                                                                    Подкатегорий: {subcategories.length}
                                                                </Typography>
                                                            </Stack>
                                                        </AccordionSummary>
                                                        <AccordionDetails>
                                                            {!isCategoryExpanded ? null : (
                                                                <Stack spacing={2}>
                                                                    <Card variant="outlined">
                                                                        <CardContent sx={{ p: 0 }}>
                                                                            <TableContainer>
                                                                                <Table size="small">
                                                                                    <TableHead>
                                                                                        <TableRow>
                                                                                            <TableCell sx={{ minWidth: 260 }}>Тариф категории</TableCell>
                                                                                            {priceRanges.map((range) => (
                                                                                                <TableCell key={`${categoryPanelKey}-${range.key}`} align="center" sx={{ minWidth: 140 }}>
                                                                                                    {range.label}
                                                                                                </TableCell>
                                                                                            ))}
                                                                                        </TableRow>
                                                                                    </TableHead>
                                                                                    <TableBody>
                                                                                        <TableRow sx={{ backgroundColor: 'action.hover' }}>
                                                                                            <TableCell>
                                                                                                <Typography variant="body1" sx={{ fontWeight: 700 }}>
                                                                                                    {category.title || category.id || 'Категория без названия'}
                                                                                                </Typography>
                                                                                            </TableCell>
                                                                                            {priceRanges.map((range) => (
                                                                                                <TableCell key={`${categoryKey}-${range.key}`} align="center">
                                                                                                    <TextField
                                                                                                        size="small"
                                                                                                        type="number"
                                                                                                        value={categoryTariffsInput[categoryKey]?.[range.key] ?? ''}
                                                                                                        onChange={(event) => handleCategoryTariffInputChange(categoryKey, range.key, event.target.value)}
                                                                                                        placeholder={String(defaultTariffRub)}
                                                                                                        inputProps={{ min: 0, step: 1 }}
                                                                                                        sx={{ width: 110 }}
                                                                                                    />
                                                                                                </TableCell>
                                                                                            ))}
                                                                                        </TableRow>
                                                                                    </TableBody>
                                                                                </Table>
                                                                            </TableContainer>
                                                                        </CardContent>
                                                                    </Card>

                                                                    {subcategories.length === 0 ? (
                                                                        <Alert severity="info">У категории нет подкатегорий.</Alert>
                                                                    ) : (
                                                                        <Card variant="outlined">
                                                                            <CardContent sx={{ p: 0 }}>
                                                                                <TableContainer>
                                                                                    <Table size="small">
                                                                                        <TableHead>
                                                                                            <TableRow>
                                                                                                <TableCell sx={{ minWidth: 260 }}>Тарифы подкатегорий</TableCell>
                                                                                                {priceRanges.map((range) => (
                                                                                                    <TableCell key={`${categoryPanelKey}-sub-${range.key}`} align="center" sx={{ minWidth: 140 }}>
                                                                                                        {range.label}
                                                                                                    </TableCell>
                                                                                                ))}
                                                                                            </TableRow>
                                                                                        </TableHead>
                                                                                        <TableBody>
                                                                                            {subcategories.map((subcategory, subcategoryIndex) => {
                                                                                                const subcategoryKey = normalizeEntityKey(subcategory.id || subcategory.title);
                                                                                                const subcategoryRowKey = `${categoryPanelKey}::sub::${subcategory.id || subcategory.title || subcategoryIndex}`;
                                                                                                return (
                                                                                                    <TableRow key={subcategoryRowKey}>
                                                                                                        <TableCell>
                                                                                                            <Typography variant="body2">
                                                                                                                {subcategory.title || subcategory.id || 'Подкатегория без названия'}
                                                                                                            </Typography>
                                                                                                        </TableCell>
                                                                                                        {priceRanges.map((range) => {
                                                                                                            const categoryFallback = getParsedInputValue(categoryTariffsInput, categoryKey, range.key) ?? defaultTariffRub;
                                                                                                            return (
                                                                                                                <TableCell key={`${subcategoryRowKey}-${range.key}`} align="center">
                                                                                                                    <TextField
                                                                                                                        size="small"
                                                                                                                        type="number"
                                                                                                                        value={subcategoryTariffsInput[subcategoryKey]?.[range.key] ?? ''}
                                                                                                                        onChange={(event) => handleSubcategoryTariffInputChange(subcategoryKey, range.key, event.target.value)}
                                                                                                                        placeholder={String(categoryFallback)}
                                                                                                                        inputProps={{ min: 0, step: 1 }}
                                                                                                                        sx={{ width: 110 }}
                                                                                                                    />
                                                                                                                </TableCell>
                                                                                                            );
                                                                                                        })}
                                                                                                    </TableRow>
                                                                                                );
                                                                                            })}
                                                                                        </TableBody>
                                                                                    </Table>
                                                                                </TableContainer>
                                                                            </CardContent>
                                                                        </Card>
                                                                    )}
                                                                </Stack>
                                                            )}
                                                        </AccordionDetails>
                                                    </Accordion>
                                                );
                                            })}
                                        </Stack>
                                    )}
                                </AccordionDetails>
                            </Accordion>
                        );
                    })}
                </Stack>
            )}
        </Box>
    );
};
